import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CSVLink } from "react-csv";
import { Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import { Skeleton } from "primereact/skeleton";
import CourseService from "../../../services/CourseService";
import VMService from "../../../services/VMService";
import { Dropdown } from "primereact/dropdown";

const VmLoginReport = () => {

  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [results, setResults] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [details, setDetails] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const headers = [
    { label: "VM User Name", key: "vmUserName" },
    { label: "User Name", key: "userName" },
    { label: "Display Name", key: "displayName" },
    { label: "Duration(mins)", key: "durationInMinutes" }
  ];

  const detailHeaders = [
    { label: "Course", key: "courseName" },
    { label: "VM User", key: "vmUserName" },
    { label: "Start Time", key: "startDateTime" },
    { label: "End Time", key: "endDateTime" },
    { label: "Duration(mins)", key: "durationInMinutes" }
  ];

  const supportAccess = authContext.getPermissions("report");

  const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  try {
    const clean = dateString.split(".")[0] + "Z";
    const date = new Date(clean);
    if (isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-IN", { month: "short" });
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    hours = String(hours).padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  } catch {
    return "-";
  }
};

  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
  }, [supportAccess, authContext, navigate]);

  // Load Courses
 const loadCourses = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await CourseService.GetCourses(authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setCourses(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

  // Load Report
const loadResults = useCallback(async (signal) => { 
    setLoading(true);
    setResults([]);
    try {
      const data = await VMService.getReport(courseId, authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setResults(data.data);
        setCurrentPage(0);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [courseId, authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal);
    return () => controller.abort(); 
  }, [loadCourses]);

useEffect(() => {
    const controller = new AbortController(); 
    if (courseId) loadResults(controller.signal); 
    return () => controller.abort();
  }, [loadResults, courseId]);

const onRowClick = async (rowData) => {
  setShowModal(true);    
  setDetailLoading(true);    
  setDetails([]);

  const data = await VMService.getVmUserDetails(
    rowData.courseUserId,
    rowData.vmId,
    authContext
  );

  setDetailLoading(false);

  if (data.isSuccess) {
    setDetails(data.data);
  } else {
    AlertService.error(data.errorMessage);
  }
};

  // Sorting
  const sortedResults = useMemo(() => {

    let sorted = [...results];

    if (sortField) {

      sorted.sort((a, b) => {

        const valA = a[sortField];
        const valB = b[sortField];

        if (valA == null) return -1;
        if (valB == null) return 1;

        return (valA < valB ? -1 : valA > valB ? 1 : 0) * (sortOrder === 1 ? 1 : -1);

      });

    }

    return sorted;

  }, [results, sortField, sortOrder]);

  // Pagination
  const paginatedData = useMemo(() => {

    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return sortedResults.slice(startIndex, endIndex);

  }, [sortedResults, currentPage, rowsPerPage]);

  const generatePageNumbers = () => {
    const totalPages = Math.max(
      1,
      Math.ceil(sortedResults.length / rowsPerPage)
    );
     if (totalPages === 1) return [1];
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");
      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);
      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages) start = totalPages - 3;
      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) pages.push(i);
      }
      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  // SKELETON UI
  const skeletonData = Array.from({ length: rowsPerPage }, (_, i) => ({ id: `skeleton-${i}` }));
  const skeletonBody = () => <Skeleton width="90%" height="1.5rem" />;

  return (
    <div
      className="container-fluid custom-container position-relative mb-5">
      <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center mb-3">
        <div className="d-flex flex-wrap gap-4 pt-3">
   <div className="flex-grow-1"  style={{ minWidth: "220px", maxWidth: "300px" }}>
  <Dropdown
    value={courseId || null}
    onChange={(e) => setCourseId(Number(e.value))}
    options={courses}
    optionLabel="label"
    optionValue="value"
    placeholder="Select a course"
    filter
    filterPlaceholder="Search course..."
    className="custom-input-color report-dropdown"
    panelClassName="report-dropdown-panel"
    emptyMessage="No courses found"
    emptyFilterMessage="No matching course found"
  />
          
          </div>
          <CSVLink
            data={results}
            headers={headers}
            filename={"VM-Login-Report.csv"}
            className="btn btn-primary"
          >
            <i className="pi pi-upload me-2"></i>Export
          </CSVLink>
        </div>
      </div>
        <DataTable
          value={loading ? skeletonData : paginatedData}
          emptyMessage={loading ? "" : "No data found"}
          className="custom-table cursor-pointer"
          responsiveLayout="scroll"
          onRowClick={(e) => onRowClick(e.data)}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
        >

          <Column
            header="S. No."
            body={
              loading
                ? skeletonBody
                : (_rowData, { rowIndex }) =>
                    currentPage * rowsPerPage + rowIndex + 1
            }
            style={{ width: "80px", textAlign: "center" }}
          />

          <Column
            field="vmUserName"
            header="VM User Name"
            sortable
            body={loading ? skeletonBody : rowData => rowData.vmUserName}
          />

          <Column
            field="userName"
            header="Username"
            sortable
            body={loading ? skeletonBody : rowData => rowData.userName}
          />

          <Column
            field="displayName"
            header="Display Name"
            sortable
            body={loading ? skeletonBody : rowData => rowData.displayName}
          />

          <Column
            field="durationInMinutes"
            header="Duration(mins)"
            body={loading ? skeletonBody : rowData => rowData.durationInMinutes}
            bodyClassName="text-center"
            style={{ width: "100px", textAlign: "center" }}
          />
        </DataTable>

        <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">

          <div className="d-flex align-items-center">
            <label className="me-2 fw-bold">Show Rows:</label>
            {loading ? (
              <Skeleton width="100px" height="38px" />
            ) : (
              <select
                className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
                value={rowsPerPage}
                onChange={e => {
                  setRowsPerPage(parseInt(e.target.value));
                  setCurrentPage(0);
                }}
              >
                {[10, 50, 100].map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
          {/* Pagination Numbers */}
          {loading ? (
            <div className="d-flex gap-2">
              {Array.from({ length: 5 }).map((_, idx) => (
                <Skeleton key={idx} width="40px" height="40px" />
              ))}
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center gap-2">
              <button
                className="btn p-0 fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              >
                &lt;
              </button>
              {generatePageNumbers().map((page, index) =>
                page === "..." ? (
                  <span key={index} className="mx-1 text-secondary">
                    ...
                  </span>
                ) : (
                  <button
                    key={index}
                    className={`btn p-0 pagination-item ${currentPage + 1 === page ? "active-page" : ""}`}
                    style={{ width: "40px", height: "40px" }}
                    onClick={() => setCurrentPage(page - 1)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                className="btn p-0 fs-5 pagination-item"
                style={{ width: "40px", height: "40px" }}
                onClick={() =>
                  setCurrentPage(prev =>
                    Math.min(Math.max(1, Math.ceil(sortedResults.length / rowsPerPage)) - 1, prev + 1)
                  )
                }
                disabled={currentPage >= Math.max(1, Math.ceil(sortedResults.length / rowsPerPage)) - 1}
              >
                &gt;
              </button>
            </div>
          )}

        </div>
      <Modal backdrop="static"
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
      >

    <Modal.Header  closeButton className="justify-content-center">
   <Modal.Title className="w-100 text-center">VM Login Details</Modal.Title>
</Modal.Header>
<div className="d-flex justify-content-end px-3 pt-3">
  <CSVLink
    data={details}
    headers={detailHeaders}
    filename={"VM-Login-Details.csv"}
    className="btn btn-primary"
  >
    <i className="pi pi-upload me-2"></i>Export
  </CSVLink>
</div>
     <Modal.Body style={{ maxHeight: "70vh", overflowY: "hidden" }}>
<DataTable
  value={detailLoading ? Array.from({ length: 5 }, (_, i) => ({ id: `skeleton-${i}` })) : details}
  className="custom-table"
  emptyMessage="No details found"
  scrollable
  scrollHeight="55vh"
>
  <Column field="courseName" header="Course" body={detailLoading ? skeletonBody : undefined} />
  <Column field="vmUserName" header="VM User" body={detailLoading ? skeletonBody : undefined} />
  <Column field="startDateTime" header="Start Time" body={detailLoading ? skeletonBody : (row) => formatDateTime(row.startDateTime)} />
  <Column field="endDateTime" header="End Time" body={detailLoading ? skeletonBody : (row) => formatDateTime(row.endDateTime)} />
  <Column field="durationInMinutes" header="Duration(mins)" body={detailLoading ? skeletonBody : undefined} />
</DataTable>
</Modal.Body>

        <Modal.Footer>

          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>

        </Modal.Footer>

      </Modal>

    </div>

  );

};

export default VmLoginReport;